/**
 * Tasks — consultant workplan.
 *
 * Header stat cards (due today / overdue / open / done this week), a
 * filterable task table with inline complete-checkbox, snooze (+1 day)
 * and cancel actions, plus a "New task" modal.
 */
import { useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import { tasksService } from '../lib/services';
import { useApi } from '../lib/hooks/useApi';
import { useToast } from '../lib/toast';
import { useDirectoryNames } from '../lib/useDirectoryNames';
import LookupField from '../components/LookupField';
import { useConsultantOptions, useCustomerOptions, useLeadOptions } from '../lib/useDirectory';
import CompleteTaskModal from '../components/CompleteTaskModal';
import { orgWallClockToISO, orgTimezoneAbbrev, partsInOrgTz, formatDateTime } from '../lib/datetime';

/**
 * UTC instant → the `datetime-local` value that reads correctly in the ORG
 * zone (the field is written back through orgWallClockToISO, so read and write
 * must use the same clock or every edit would shift the time).
 */
function isoToLocalInput(iso) {
  const p = iso ? partsInOrgTz(iso) : null;
  if (!p) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;
// Must match the backend enums (LeadTask.Type / LeadTask.Status); anything else
// is rejected with a 400 or can never match a stored status.
const TYPE_OPTIONS = ['CALL', 'EMAIL', 'SMS', 'FOLLOW_UP', 'ADMIN', 'DELIVERY_PREP'];
const PRIORITY_OPTIONS = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
const STATUS_OPTIONS = ['OPEN', 'SNOOZED', 'DONE', 'CANCELLED'];
// The list endpoint pages with `size` (default 20). Ask for enough that the
// stat cards, which are computed client-side, see every task.
const TASK_FETCH_SIZE = 500;

const PRIORITY_TONE = { LOW: '', NORMAL: 'info', HIGH: 'warning', URGENT: 'danger' };
const STATUS_TONE = { OPEN: 'info', SNOOZED: 'accent', DONE: 'success', CANCELLED: 'danger' };

function isOpen(t) { return t.status === 'OPEN' || t.status === 'SNOOZED'; }
function isOverdue(t) { return isOpen(t) && t.dueAt && new Date(t.dueAt).getTime() < Date.now(); }
function relDue(iso) {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  const m = Math.round(abs / 60000);
  let label;
  if (m < 60) label = `${m}m`;
  else if (m < 60 * 24) label = `${Math.round(m / 60)}h`;
  else label = `${Math.round(m / (60 * 24))}d`;
  return diff < 0 ? `${label} overdue` : `in ${label}`;
}
// A finished task is not overdue: show its due date as history instead.
function dueLabel(t) {
  const rel = relDue(t.dueAt);
  if (isOpen(t) || !t.dueAt) return rel;
  return rel.endsWith(' overdue') ? `Due ${rel.replace(' overdue', ' ago')}` : `Due ${rel}`;
}
function asList(d) {
  if (Array.isArray(d)) return d;
  return d?.content ?? d?.tasks ?? [];
}

/* ── mock fallback ── */
function mkMock() {
  const now = Date.now();
  const mk = (id, title, type, priority, status, dueOffMs, who, extra = {}) => ({
    id, title, type, priority, status,
    dueAt: new Date(now + dueOffMs).toISOString(),
    assigneeId: who.toLowerCase().replace(/\s+/g, '-'),
    assigneeName: who,
    ...extra,
  });
  return [
    mk('task-1',  'Call back about RAV4 quote',        'CALL',      'HIGH',   'OPEN',        2 * 3600e3,  'Mia Chen',   { leadId: 'lead-101' }),
    mk('task-2',  'Send finance pre-approval pack',    'EMAIL',     'URGENT', 'OPEN',       -3 * 3600e3,  'Josh Patel', { customerId: 'cust-77' }),
    mk('task-3',  'Follow up test-drive feedback',     'FOLLOW_UP', 'NORMAL', 'OPEN',        6 * 3600e3,  'Mia Chen',   { leadId: 'lead-108' }),
    mk('task-4',  'Book delivery detailing',           'ADMIN',     'LOW',    'SNOOZED',     1 * DAY_MS,  'Grace Kim',  { customerId: 'cust-45' }),
    mk('task-5',  'Chase trade-in payout letter',      'CALL',      'HIGH',   'OPEN',       -1 * DAY_MS,  'Josh Patel', { leadId: 'lead-102' }),
    mk('task-6',  'Prep appraisal for CX-5',           'ADMIN',     'NORMAL', 'DONE',       -2 * 3600e3,  'Grace Kim',  { completedAt: new Date(now - 2 * 3600e3).toISOString() }),
    mk('task-7',  'Meeting: fleet buyer intro',        'FOLLOW_UP',   'HIGH',   'OPEN',        2 * DAY_MS,  'Mia Chen',   { customerId: 'cust-90' }),
    mk('task-8',  'Email service-due reminder batch',  'EMAIL',     'LOW',    'DONE',       -1 * DAY_MS,  'Josh Patel', { completedAt: new Date(now - DAY_MS).toISOString() }),
    mk('task-9',  'Re-engage cold lead (Kluger)',      'FOLLOW_UP', 'NORMAL', 'OPEN',        3 * DAY_MS,  'Grace Kim',  { leadId: 'lead-115' }),
    mk('task-10', 'Cancel duplicate enquiry follow-up','ADMIN',     'LOW',    'CANCELLED',  -4 * DAY_MS,  'Mia Chen',   {}),
  ];
}
const MOCK_TASKS = mkMock();

/* ── new / edit task modal ── */
// `task` set → edit that task (any status except Cancelled, which the server
// refuses); omitted → create.
function NewTaskModal({ task = null, onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState(() => ({
    title: task?.title || '',
    type: task?.type || 'CALL',
    priority: task?.priority || 'NORMAL',
    dueAt: isoToLocalInput(task?.dueAt),
    notes: task?.notes || '',
    assigneeId: task?.assigneeId || '',
    leadId: task?.leadId || '',
    customerId: task?.customerId || '',
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Directory options for the searchable pickers. Mounted only while the modal
  // is open (the parent conditionally renders it), so these fetch on open.
  const assigneesQ = useConsultantOptions();
  const customersQ = useCustomerOptions();
  const leadsQ = useLeadOptions();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const save = task ? (body) => tasksService.update(task.id, body) : tasksService.create;
      await save({
        title: form.title.trim(),
        type: form.type,
        priority: form.priority,
        // orgWallClockToISO, not new Date(...).toISOString(): the input reads as
        // dealership wall-clock time. Converting in the browser's zone set the
        // due time — and therefore the reminder — to the wrong hour for anyone
        // working outside the dealership's zone.
        dueAt: form.dueAt ? orgWallClockToISO(form.dueAt) : null,
        notes: form.notes.trim() || null,
        assigneeId: form.assigneeId || null,
        leadId: form.leadId || null,
        customerId: form.customerId || null,
      });
      toast(task ? 'Task updated' : 'Task created', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      toast(`${task ? 'Update' : 'Create'} failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(520px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">{task ? 'Edit task' : 'New task'}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-field">
            <label>Title *</label>
            <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Call back about…" autoFocus required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label>Type</label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)}>
                {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Priority</label>
              <select value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <LookupField
            id="ntm-assignee"
            label="Assign to"
            placeholder="Search team members…"
            emptyHint="No team member matches"
            options={assigneesQ.options}
            loading={assigneesQ.loading}
            error={assigneesQ.error}
            value={form.assigneeId || null}
            onChange={(id) => set('assigneeId', id || '')}
          />

          <div className="form-field">
            <label>Due <span className="text-muted">({orgTimezoneAbbrev()})</span></label>
            <input type="datetime-local" value={form.dueAt} onChange={(e) => set('dueAt', e.target.value)} />
          </div>

          <LookupField
            id="ntm-lead"
            label="Lead (optional)"
            placeholder="Search by customer, vehicle or source…"
            emptyHint="No lead matches"
            options={leadsQ.options}
            loading={leadsQ.loading}
            value={form.leadId || null}
            onChange={(id, opt) => {
              // Picking a lead auto-fills its customer when the task isn't
              // already linked to one.
              setForm((f) => ({
                ...f,
                leadId: id || '',
                customerId: f.customerId || opt?.customerId || '',
              }));
            }}
          />

          <LookupField
            id="ntm-customer"
            label="Customer (optional)"
            placeholder="Search by name, phone or email…"
            emptyHint="No customer matches"
            options={customersQ.options}
            loading={customersQ.loading}
            value={form.customerId || null}
            onChange={(id) => set('customerId', id || '')}
          />

          <div className="form-field">
            <label>Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy || !form.title.trim()}>
            {busy ? 'Saving…' : (task ? 'Save changes' : 'Create task')}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── page ── */
export default function Tasks() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [dueFilter, setDueFilter] = useState('ALL'); // TODAY | WEEK | ALL
  const [modalOpen, setModalOpen] = useState(false);
  const [detail, setDetail] = useState(null);       // task shown in the detail view
  const [editing, setEditing] = useState(null);     // task open in the edit form
  const [completing, setCompleting] = useState(null); // lead-linked task being completed

  const { data, loading, refetch } = useApi(
    () => tasksService.list({ page: 0, size: TASK_FETCH_SIZE }),
    { devFallback: MOCK_TASKS },
  );
  const names = useDirectoryNames();

  // Resolve the assignee (a user) and the linked lead/customer into names.
  const tasks = useMemo(() => asList(data).map((t) => ({
    ...t,
    _assignee: names.consultant(t.assigneeId, t.assigneeName, { dash: null }),
    _ref: t.leadId
      ? names.lead(t.leadId, t.leadName, { dash: null })
      : names.customer(t.customerId, t.customerName, { dash: null }),
  })), [data, names]);

  const assignees = useMemo(
    () => Array.from(new Set(tasks.map((t) => t._assignee).filter(Boolean))).sort(),
    [tasks],
  );

  const stats = useMemo(() => {
    const now = new Date();
    const dayStart = new Date(now); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + DAY_MS);
    const weekAgo = new Date(now.getTime() - 7 * DAY_MS);
    return {
      dueToday: tasks.filter((t) => isOpen(t) && t.dueAt && new Date(t.dueAt) >= dayStart && new Date(t.dueAt) < dayEnd).length,
      overdue: tasks.filter(isOverdue).length,
      open: tasks.filter(isOpen).length,
      doneWeek: tasks.filter((t) => {
        if (t.status !== 'DONE') return false;
        const done = new Date(t.completedAt || t.updatedAt || t.dueAt || 0);
        return done >= weekAgo;
      }).length,
    };
  }, [tasks]);

  const filtered = useMemo(() => {
    const now = new Date();
    const dayStart = new Date(now); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + DAY_MS);
    const weekEnd = new Date(dayStart.getTime() + 7 * DAY_MS);
    return tasks.filter((t) => {
      if (statusFilter && t.status !== statusFilter) return false;
      if (assigneeFilter && t._assignee !== assigneeFilter) return false;
      if (dueFilter !== 'ALL') {
        if (!t.dueAt) return false;
        const due = new Date(t.dueAt);
        if (dueFilter === 'TODAY' && (due < dayStart || due >= dayEnd)) return false;
        if (dueFilter === 'WEEK' && (due < dayStart || due >= weekEnd)) return false;
      }
      return true;
    }).sort((a, b) => new Date(a.dueAt || 8.64e15) - new Date(b.dueAt || 8.64e15));
  }, [tasks, statusFilter, assigneeFilter, dueFilter]);

  const patch = async (t, body, okMsg) => {
    try {
      await tasksService.update(t.id, body);
      toast(okMsg, { variant: 'success' });
      refetch();
    } catch (e) {
      toast(`Update failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };
  const complete = (t) => patch(t, { status: 'DONE' }, `Done: ${t.title}`);
  const reopen = (t) => patch(t, { status: 'OPEN' }, `Reopened: ${t.title}`);
  // A lead-linked task goes through the completion dialog, which offers to set
  // the lead's next step. A plain tick would leave Lead Accelerator, Sales
  // Accelerator and Customer 360 showing "No next step" for that lead.
  // (The server's /complete only accepts an OPEN task, so a snoozed one is
  // completed with a plain status update.)
  const startComplete = (t) => (t.leadId && t.status === 'OPEN' ? setCompleting(t) : complete(t));
  const cancel = (t) => patch(t, { status: 'CANCELLED' }, `Cancelled: ${t.title}`);
  const snooze = (t) => {
    const base = t.dueAt ? Math.max(new Date(t.dueAt).getTime(), Date.now()) : Date.now();
    patch(t, { dueAt: new Date(base + DAY_MS).toISOString() }, `Snoozed +1 day: ${t.title}`);
  };

  return (
    <>
      <TopBar
        crumbs={['Tasks']}
        right={
          <>
            <button className="btn small" onClick={refetch}>↻ Refresh</button>
            <button className="btn small primary" onClick={() => setModalOpen(true)}>+ New task</button>
          </>
        }
      />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <StatCard label="Due today" value={stats.dueToday} color="var(--info)" />
          <StatCard label="Overdue" value={stats.overdue} color="var(--danger)" />
          <StatCard label="Open" value={stats.open} color="var(--text-primary)" />
          <StatCard label="Done this week" value={stats.doneWeek} color="var(--success)" />
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="btn small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select className="btn small" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
            <option value="">All assignees</option>
            {assignees.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 4 }}>
            {['TODAY', 'WEEK', 'ALL'].map((d) => (
              <button key={d} className={`btn small ${dueFilter === d ? 'primary' : ''}`} onClick={() => setDueFilter(d)}>
                {d === 'TODAY' ? 'Today' : d === 'WEEK' ? 'Week' : 'All'}
              </button>
            ))}
          </div>
          {loading && <span className="mono text-xs text-muted">loading…</span>}
          <span style={{ marginLeft: 'auto' }} className="mono text-xs text-muted">{filtered.length} tasks</span>
        </div>

        {/* Table */}
        <div className="card" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div className="empty-state" style={{ padding: 40 }}>
              <div className="icon">✓</div>
              <div className="title">No tasks match</div>
              <div className="desc">Clear the filters or create a new task.</div>
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Ref</th>
                  <th>Due</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th style={{ width: 150 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={t.status === 'DONE'}
                        disabled={t.status === 'CANCELLED'}
                        onChange={() => (t.status === 'DONE' ? reopen(t) : startComplete(t))}
                        aria-label={`${t.status === 'DONE' ? 'Reopen' : 'Complete'} ${t.title}`}
                        style={{ accentColor: 'var(--success)', cursor: t.status === 'CANCELLED' ? 'default' : 'pointer' }}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => setDetail(t)}
                        title="Open task details"
                        style={{
                          all: 'unset', cursor: 'pointer', fontWeight: 600,
                          textDecoration: t.status === 'CANCELLED' ? 'line-through' : 'none',
                        }}
                      >
                        {t.title}
                      </button>
                      <div className="mono text-xs text-muted">{t._assignee || 'unassigned'}</div>
                    </td>
                    <td><span className="pill">{(t.type || '—').replace('_', ' ')}</span></td>
                    <td className="mono text-xs text-muted">{t._ref || '—'}</td>
                    <td style={{ color: isOverdue(t) ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: isOverdue(t) ? 600 : 400 }}>
                      {dueLabel(t)}
                    </td>
                    <td>
                      <span className={`pill filled ${PRIORITY_TONE[t.priority] || ''}`}>{t.priority || '—'}</span>
                    </td>
                    <td>
                      <span className={`pill ${STATUS_TONE[t.status] || ''}`}>{(t.status || '—').replace('_', ' ')}</span>
                    </td>
                    <td>
                      {isOpen(t) && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn small" onClick={() => snooze(t)} title="Snooze +1 day">+1d</button>
                          <button className="btn small danger" onClick={() => cancel(t)}>Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && <NewTaskModal onClose={() => setModalOpen(false)} onCreated={refetch} />}
      {editing && <NewTaskModal task={editing} onClose={() => setEditing(null)} onCreated={refetch} />}

      {detail && (
        <TaskDetailModal
          task={detail}
          assignee={detail._assignee}
          refLabel={detail._ref}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditing(detail); setDetail(null); }}
          onComplete={() => { startComplete(detail); setDetail(null); }}
          onReopen={() => { reopen(detail); setDetail(null); }}
          onCancel={() => { cancel(detail); setDetail(null); }}
        />
      )}

      <CompleteTaskModal
        open={!!completing}
        task={completing}
        lead={completing ? { id: completing.leadId, customerId: completing.customerId } : null}
        onClose={() => setCompleting(null)}
        onCompleted={refetch}
      />
    </>
  );
}

/* ── task detail view: read the task, then edit / complete / reopen / cancel ── */
function TaskDetailModal({ task: t, assignee, refLabel, onClose, onEdit, onComplete, onReopen, onCancel }) {
  const closed = t.status === 'CANCELLED';
  const row = (label, value) => (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
      <div className="card-label">{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ width: 'min(520px, 92vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{t.title}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {row('Status', <span className={`pill ${STATUS_TONE[t.status] || ''}`}>{(t.status || '—').replace('_', ' ')}</span>)}
          {row('Type', (t.type || '').replace('_', ' '))}
          {row('Priority', <span className={`pill filled ${PRIORITY_TONE[t.priority] || ''}`}>{t.priority}</span>)}
          {row('Due', t.dueAt ? `${formatDateTime(t.dueAt)} (${dueLabel(t)})` : null)}
          {row('Completed', t.completedAt ? formatDateTime(t.completedAt) : null)}
          {row('Assignee', assignee)}
          {row('Related', refLabel)}
          {row('Notes', t.notes)}
        </div>
        <div className="modal-footer">
          {!closed && <button type="button" className="btn small" onClick={onEdit}>Edit</button>}
          {isOpen(t) && <button type="button" className="btn small danger" onClick={onCancel}>Cancel task</button>}
          {isOpen(t) && <button type="button" className="btn small primary" onClick={onComplete}>Mark done</button>}
          {t.status === 'DONE' && <button type="button" className="btn small primary" onClick={onReopen}>Reopen</button>}
          <button type="button" className="btn small" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div className="card-label">{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
